import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { scroller } from 'react-scroll';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import ScrollAwareLink from '../ScrollAwareLink';
import api from '../../services/api';
import LanguageSwitcher from './LanguageSwitcher';

const HeaderContainer = styled.header`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-bottom: 2px solid #043663;
  box-shadow: 0 4px 6px -1px rgba(4, 54, 99, 0.1);
  position: ${(p) => (p.$embedded ? 'relative' : 'fixed')};
  top: ${(p) => (p.$embedded ? 'auto' : '0')};
  left: ${(p) => (p.$embedded ? 'auto' : '0')};
  right: ${(p) => (p.$embedded ? 'auto' : '0')};
  z-index: ${(p) => (p.$embedded ? 'auto' : '10001')}; /* Higher than mobile menu when standalone */
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 8px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  min-height: 70px;
  
  @media (max-width: 768px) {
    justify-content: space-between;
    padding: 8px 12px;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  justify-content: flex-end;
  min-width: 0;
  flex: 1;

  @media (min-width: 769px) {
    flex-wrap: nowrap;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
  }
  
  @media (max-width: 1200px) {
    gap: 10px;
  }
  
  @media (max-width: 768px) {
    gap: 12px;
    flex-wrap: nowrap;
    flex: 0 auto;
    overflow-x: visible;
  }
`;

const DesktopOnlyHeaderButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  flex-wrap: nowrap;

  @media (max-width: 768px) {
    display: none;
  }
`;

const LogoBrandTitle = styled.span`
  line-height: 1.2;
  min-width: 0;
  max-width: 13rem;

  @media (max-width: 1150px) and (min-width: 769px) {
    font-size: 0.95rem;
    max-width: 11rem;
  }
`;

const Logo = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  min-width: 0;
  font-weight: 700;
  font-size: 1.2rem;
  color: var(--brand-blue);
  text-decoration: none;
  
  img {
    flex-shrink: 0;
    border-radius: 8px;
    object-fit: contain;
    height: 50px;
    width: auto;
  }
  
  @media (max-width: 768px) {
    gap: 6px;
    font-size: 1rem;
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: nowrap;
  justify-content: flex-end;
  min-width: 0;
  
  @media (max-width: 768px) {
    display: none;
  }
  
  @media (max-width: 1200px) {
    gap: 8px;
  }

  @media (max-width: 1150px) and (min-width: 769px) {
    font-size: 0.8125rem;
    gap: 6px 10px;
  }
`;

const NavLink = styled(Link)`
  text-decoration: none;
  color: var(--text-primary);
  font-weight: 500;
  transition: color 0.3s ease;
  
  &:hover {
    color: var(--brand-blue);
  }
`;

const Button = styled(Link).withConfig({
  shouldForwardProp: (prop) => !['primary'].includes(prop),
})`
  display: inline-block;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  text-decoration: none;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  white-space: nowrap;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    font-size: 0.875rem;
    padding: 10px 16px;
  }
  
  ${props => props.$primary ? `
    background: linear-gradient(135deg, #043663 0%, #0a4d7a 100%);
    color: white;
    border: 2px solid #043663;
    
    &:hover {
      background: linear-gradient(135deg, #032a4a 0%, #043663 100%);
      transform: translateY(-2px);
      box-shadow: 0 10px 15px -3px rgba(4, 54, 99, 0.3);
    }

    @media (max-width: 1150px) and (min-width: 769px) {
      padding: 9px 14px;
      font-size: 0.8125rem;
    }
  ` : props.$secondary ? `
    background: linear-gradient(135deg, #d52b1e 0%, #e6392a 100%);
    color: white;
    border: 2px solid #d52b1e;
    
    &:hover {
      background: linear-gradient(135deg, #b8241a 0%, #d52b1e 100%);
      transform: translateY(-2px);
      box-shadow: 0 10px 15px -3px rgba(213, 43, 30, 0.3);
    }

    @media (max-width: 1150px) and (min-width: 769px) {
      padding: 9px 14px;
      font-size: 0.8125rem;
    }
  ` : props.$small ? `
    background: linear-gradient(135deg, #043663 0%, #0a4d7a 100%);
    color: white;
    border: 2px solid #043663;
    font-size: 0.875rem;
    padding: 10px 16px;
    
    @media (max-width: 768px) {
      font-size: 0.75rem;
      padding: 8px 12px;
    }
    
    &:hover {
      background: linear-gradient(135deg, #032a4a 0%, #043663 100%);
      transform: translateY(-2px);
      box-shadow: 0 10px 15px -3px rgba(4, 54, 99, 0.3);
    }

    @media (max-width: 1150px) and (min-width: 769px) {
      padding: 9px 14px;
      font-size: 0.8125rem;
    }
  ` : `
    background-color: transparent;
    color: #043663;
    border: 2px solid #043663;
    
    &:hover {
      background: linear-gradient(135deg, #043663 0%, #0a4d7a 100%);
      color: white;
      transform: translateY(-2px);
    }

    @media (max-width: 1150px) and (min-width: 769px) {
      padding: 9px 14px;
      font-size: 0.8125rem;
    }
  `}
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const UserName = styled.span`
  color: #1f2937;
  font-weight: 500;
`;

const LogoutButton = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  font-size: 0.875rem;
  text-decoration: underline;
  padding: 0;
  
  &:hover {
    color: #043663;
  }
`;

const MobileToggle = styled.button`
  display: none;
  flex-direction: column;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  margin-left: auto;
  
  @media (max-width: 768px) {
    display: flex;
  }
  
  span {
    width: 25px;
    height: 3px;
    background-color: #1f2937;
    margin: 3px 0;
    transition: 0.3s;
  }
`;

const MobileMenu = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isOpen'].includes(prop),
})`
  display: none;
  position: fixed;
  top: 70px; /* Height of the header */
  left: 0;
  right: 0;
  background-color: #ffffff;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  padding: 20px;
  flex-direction: column;
  gap: 16px;
  z-index: 10000; /* Even higher z-index to appear above everything */
  
  ${props => props.$isOpen && `
    display: flex;
  `}
`;

const ContactLink = ({ children, onClick }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleContactClick = (e) => {
    e.preventDefault();
    
    console.log('🔍 Contact button clicked!');
    console.log('📍 Current pathname:', location.pathname);
    
    if (location.pathname === '/') {
      console.log('🏠 Already on home page, using react-scroll...');
      
      // Debug: Check if contact element exists
      const contactElement = document.getElementById('contact');
      console.log('🎯 Contact element found:', !!contactElement);
      if (contactElement) {
        const rect = contactElement.getBoundingClientRect();
        console.log('📍 Contact element position:', {
          top: rect.top,
          left: rect.left,
          height: rect.height,
          width: rect.width
        });
      }
      
      // Get responsive offset based on screen size
      const isMobile = window.innerWidth <= 768;
      const offset = isMobile ? 200 : -120; // Positive offset for mobile to scroll DOWN more
      
      console.log('📱 Device type:', isMobile ? 'Mobile' : 'Desktop');
      console.log('📏 Using offset:', offset);
      
      // Already on home page, use react-scroll to scroll to contact section
      scroller.scrollTo('contact', {
        duration: 800,
        delay: 0,
        smooth: 'easeInOutQuart',
        offset: offset, // Responsive offset for mobile vs desktop
      });
      console.log('✅ React-scroll command sent!');
    } else {
      console.log('🔄 Not on home page, navigating first...');
      // Navigate to home page first, then scroll to contact
      navigate('/');
      setTimeout(() => {
        console.log('⏰ Timeout executed, now scrolling...');
        
        // Debug: Check if contact element exists after navigation
        const contactElement = document.getElementById('contact');
        console.log('🎯 Contact element found after navigation:', !!contactElement);
        if (contactElement) {
          const rect = contactElement.getBoundingClientRect();
          console.log('📍 Contact element position after navigation:', {
            top: rect.top,
            left: rect.left,
            height: rect.height,
            width: rect.width
          });
        }
        
        // Get responsive offset based on screen size
        const isMobile = window.innerWidth <= 768;
        const offset = isMobile ? 700 : 200; // Positive offset for mobile to scroll DOWN more
        
        console.log('📱 Device type:', isMobile ? 'Mobile' : 'Desktop');
        console.log('📏 Using offset:', offset);
        
        scroller.scrollTo('contact', {
          duration: 800,
          delay: 0,
          smooth: 'easeInOutQuart',
          offset: offset, // Responsive offset for mobile vs desktop
        });
        console.log('✅ React-scroll command sent after navigation!');
      }, 500); // Increased from 100ms to 500ms
    }
    
    if (onClick) onClick();
  };

  return (
    <NavLink to="#" onClick={handleContactClick}>
      {children}
    </NavLink>
  );
};

const HomeLink = ({ children, onClick }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleHomeClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      // Already on home page, scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Navigate to home page and scroll to top
      navigate('/');
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
    if (onClick) onClick();
  };

  return (
    <NavLink to="#" onClick={handleHomeClick}>
      {children}
    </NavLink>
  );
};

const Header = ({ embedded = false }) => {
  const { t } = useTranslation();
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isTeammateFinderEnabled, setIsTeammateFinderEnabled] = useState(true);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Check if teammate finder feature is enabled
  useEffect(() => {
    const checkFeatureEnabled = async () => {
      try {
        const response = await api.get('/teammate-finder/feature-enabled');
        if (response.success) {
          setIsTeammateFinderEnabled(response.enabled !== false); // Default to true if not set
        }
      } catch (error) {
        console.error('Error checking teammate finder feature status:', error);
        // Default to enabled if error
        setIsTeammateFinderEnabled(true);
      }
    };
    checkFeatureEnabled();
  }, []);

  return (
    <HeaderContainer $embedded={embedded}>
      <HeaderContent>
        <Logo
          to="/"
          onClick={(e) => {
            e.preventDefault();
            if (location.pathname === '/') {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              navigate('/');
              setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }, 100);
            }
          }}
          style={{ cursor: 'pointer', transition: 'transform 0.2s ease' }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          <img 
            src="/images/logo/logo.png" 
            alt={t('header.logoAlt')}
            style={{ transition: 'transform 0.2s ease' }}
          />
          <LogoBrandTitle>{t('hero.title')}</LogoBrandTitle>
        </Logo>
        
        <HeaderRight>
          {isAuthenticated ? (
            <>
              <Nav>
                <NavLink to="/dashboard?section=overview">{t('header.overview')}</NavLink>
                <NavLink to="/dashboard?section=stats">{t('header.statistics')}</NavLink>
                <NavLink to="/dashboard?section=schedule">{t('header.schedule')}</NavLink>
                <NavLink to="/dashboard?section=team">{t('header.teamMembers')}</NavLink>
                <UserSection>
                  <UserName>{user?.teamName}</UserName>
                  <LogoutButton onClick={handleLogout}>{t('header.logout')}</LogoutButton>
                </UserSection>
              </Nav>
              <LanguageSwitcher />
            </>
          ) : (
            <>
              <Nav>
                <HomeLink>{t('header.home')}</HomeLink>
                <ScrollAwareLink to="/guide-to-hps">{t('header.guideToHps')}</ScrollAwareLink>
                <ScrollAwareLink to="/partner-clubs">{t('header.partnerships')}</ScrollAwareLink>
                <ContactLink>{t('header.contact')}</ContactLink>
              </Nav>
              <DesktopOnlyHeaderButtons>
                <Button to="/login">{t('header.login')}</Button>
                <Button $secondary to="/register-player">{t('header.register')}</Button>
                {isTeammateFinderEnabled && (
                  <Button $small to="/find-teammate">{t('header.findTeammate')}</Button>
                )}
                <LanguageSwitcher />
              </DesktopOnlyHeaderButtons>
            </>
          )}
        </HeaderRight>
        
        <MobileToggle onClick={toggleMobileMenu}>
          <span></span>
          <span></span>
          <span></span>
        </MobileToggle>
      </HeaderContent>
      
      <MobileMenu $isOpen={mobileMenuOpen}>
        <LanguageSwitcher />
        {isAuthenticated ? (
          <>
            <NavLink to="/dashboard?section=overview" onClick={() => setMobileMenuOpen(false)}>{t('header.overview')}</NavLink>
            <NavLink to="/dashboard?section=stats" onClick={() => setMobileMenuOpen(false)}>{t('header.statistics')}</NavLink>
            <NavLink to="/dashboard?section=schedule" onClick={() => setMobileMenuOpen(false)}>{t('header.schedule')}</NavLink>
            <NavLink to="/dashboard?section=team" onClick={() => setMobileMenuOpen(false)}>{t('header.teamMembers')}</NavLink>
            <UserSection>
              <UserName>{user?.teamName}</UserName>
              <LogoutButton onClick={handleLogout}>{t('header.logout')}</LogoutButton>
            </UserSection>
          </>
        ) : (
          <>
            <HomeLink onClick={() => setMobileMenuOpen(false)}>{t('header.home')}</HomeLink>
            <ScrollAwareLink to="/guide-to-hps" onClick={() => setMobileMenuOpen(false)}>{t('header.guideToHps')}</ScrollAwareLink>
            <ScrollAwareLink to="/partner-clubs" onClick={() => setMobileMenuOpen(false)}>{t('header.partnerships')}</ScrollAwareLink>
            <ContactLink onClick={() => setMobileMenuOpen(false)}>{t('header.contact')}</ContactLink>
            <Button to="/login" onClick={() => setMobileMenuOpen(false)}>{t('header.login')}</Button>
            <Button $secondary to="/register-player" onClick={() => setMobileMenuOpen(false)}>{t('header.register')}</Button>
            {isTeammateFinderEnabled && (
              <Button $small to="/find-teammate" onClick={() => setMobileMenuOpen(false)}>{t('header.findTeammate')}</Button>
            )}
          </>
        )}
      </MobileMenu>
    </HeaderContainer>
  );
};

export default Header;